import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { Tree } from '@dgreenheck/ez-tree';

// EZ-Tree (@dgreenheck/ez-tree, MIT) gives proper forking branch geometry + leaf-card placement - far
// better tree structure than our old cylinder + blob. We use it ONLY for the GEOMETRY: generate a preset
// ONCE at load, extract the branch + leaf BufferGeometry, scale it to the game's tree-size system, and
// hand it to the existing instanced NodeField pipeline (which applies our own bark/leaf materials, per-
// tree colour, wind, interior-darken, and drives harvest/regrowth/collision). EZ-Tree's own materials/
// textures are not used (its leaf material needs a caller-supplied alpha texture; we map our own leaf
// texture onto its cards instead). three is a peerDependency, so it shares our r0.184 instance.

export interface TreeParts { trunk: THREE.BufferGeometry; foliage: THREE.BufferGeometry; }

// Generate one preset tree and return its branch + leaf geometry, scaled so the whole tree is
// `targetHeight` units tall (the per-instance scale multipliers in NodeField then size it up), with the
// base sunk ~0.4 below y=0 so the trunk roots into the ground on slopes.
export function makeEzTreeParts(preset: string, seed: number, targetHeight: number): TreeParts {
  const tree = new Tree();
  tree.loadPreset(preset);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const opts = tree.options as any;
  opts.seed = seed;
  // Keep it cheap for THOUSANDS of instances drawn every frame (no per-instance cull): cap branch recursion
  // + radial segments and thin the leaf cards. A hero tree is ~9800 tris; at 4800 trees that is the whole
  // frame budget. 4 radial sides (was 6) ~= a third off the trunk; halving the leaf count ~= half the canopy
  // tris. Stylised + mostly seen at distance, so the detail drop barely reads.
  if (opts.branch) {
    opts.branch.levels = Math.min(opts.branch.levels ?? 3, 3);
    const seg = opts.branch.segments as Record<string, number> | undefined;
    if (seg) for (const k of Object.keys(seg)) seg[k] = Math.min(seg[k], 3); // 3 radial sides (was 4): leaner trunk/branches
  }
  if (opts.leaves && typeof opts.leaves.count === 'number') {
    // Leaf cards were the single biggest tree cost (~4k tris/tree). 0.3 of the preset (was 0.5) ~ a third of
    // the canopy cards: the foliage still reads as a full 3D mass (top-light + tint) but at far fewer tris.
    opts.leaves.count = Math.max(8, Math.round(opts.leaves.count * 0.3));
  }
  tree.generate();

  const trunk = tree.branchesMesh.geometry.clone();
  const foliage = tree.leavesMesh.geometry.clone();

  // Scale to the target height off the combined bounding box, then sink the base ~0.55 below y=0 (deep
  // enough that a slope-seated trunk still roots into the uphill side).
  trunk.computeBoundingBox();
  foliage.computeBoundingBox();
  const bb = trunk.boundingBox!.clone().union(foliage.boundingBox!);
  const s = targetHeight / Math.max(0.001, bb.max.y - bb.min.y);
  trunk.scale(s, s, s);
  foliage.scale(s, s, s);
  trunk.computeBoundingBox();
  foliage.computeBoundingBox();
  const baseY = Math.min(trunk.boundingBox!.min.y, foliage.boundingBox!.min.y);
  const dy = -baseY - 0.55;
  trunk.translate(0, dy, 0);
  foliage.translate(0, dy, 0);
  // (No root flare: the old skirt cone at the base read as a mismatched brown lump against the trunk --
  // owner cut it. The trunk base is already sunk 0.55 below grade, which seats it on slopes.)

  // Top-light vertex colours on the leaves (bright crown -> shaded underside) so the game's LEAF_MAT
  // (vertexColors) reads the canopy as a lit 3D mass; EZ-Tree leaves ship no colour attribute.
  addLeafTopLight(foliage);

  // Free the source meshes' GPU buffers; we kept clones.
  tree.branchesMesh.geometry.dispose();
  tree.leavesMesh.geometry.dispose();
  return { trunk, foliage };
}

// LOW-POLY far-LOD impostor for a tree species: a trunk + a canopy (cone for conifers, blob for broadleaf),
// ~30 triangles, with baked vertex colours (brown trunk / green canopy). Sized to the SAME target height as
// the full EZ-tree of that species, so the same per-instance matrix lines them up; the NodeField scales the
// impostor to nothing within the near band (where the full 3D tree draws) and up to full size out at range.
// This is what lets thousands of distant trees cost ~30 tris each instead of ~5000.
export type ImpostorKind = 'conifer' | 'broadleaf' | 'bare' | 'palm';
export function makeTreeImpostor(kind: ImpostorKind, height: number): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  // Merge needs uniform attributes: convert every primitive to NON-indexed (icosahedron is already, the
  // cylinder/cone are indexed), then bake the colour, so all parts carry position+normal+uv+color the same.
  const add = (g: THREE.BufferGeometry, hex: number): void => {
    const ni = g.index ? g.toNonIndexed() : g;
    if (ni !== g) g.dispose();
    paintImpostor(ni, hex);
    parts.push(ni);
  };
  const trunkH = height * (kind === 'palm' ? 0.7 : 0.45);
  const trunkR = height * 0.04;
  const trunk = new THREE.CylinderGeometry(trunkR * 0.65, trunkR, trunkH, 5);
  trunk.translate(0, trunkH * 0.5, 0);
  add(trunk, 0x55402a);
  if (kind === 'conifer') {
    const cone = new THREE.ConeGeometry(height * 0.28, height * 0.8, 7);
    cone.translate(0, height * 0.55, 0);
    add(cone, 0x33592c);
  } else if (kind === 'broadleaf') {
    const blob = new THREE.IcosahedronGeometry(height * 0.34, 0);
    blob.scale(1, 0.82, 1);
    blob.translate(0, height * 0.72, 0);
    add(blob, 0x3c6f2f);
  } else if (kind === 'palm') {
    const crown = new THREE.IcosahedronGeometry(height * 0.26, 0);
    crown.scale(1.3, 0.5, 1.3);
    crown.translate(0, height * 0.82, 0);
    add(crown, 0x3f7a3a);
  } // 'bare' = trunk only
  const geo = mergeGeometries(parts, false)!;
  geo.computeVertexNormals();
  return geo;
}

function paintImpostor(g: THREE.BufferGeometry, hex: number): void {
  const c = new THREE.Color(hex);
  const n = g.attributes.position.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b; }
  g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
}

function addLeafTopLight(geo: THREE.BufferGeometry): void {
  geo.computeBoundingBox();
  const y0 = geo.boundingBox!.min.y;
  const y1 = Math.max(geo.boundingBox!.max.y, y0 + 0.001);
  const pos = geo.attributes.position;
  const cols = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) - y0) / (y1 - y0);
    const b = 0.6 + 0.65 * t;
    cols[i * 3] = b;
    cols[i * 3 + 1] = b;
    cols[i * 3 + 2] = b * 0.96;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(cols, 3));
}
